// Utility functions for token inspection and testing

import { formatDateTimeInNurseryTimezone } from './timezone';

/**
 * Decode JWT token payload (client-side only, for debugging)
 */
export const decodeJWT = (token: string) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
};

/**
 * Get token expiration time as readable date
 */
export const getTokenExpiration = (token: string, nurseryTimezone: string = 'UTC'): string | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  return formatDateTimeInNurseryTimezone(new Date(decoded.exp * 1000).toISOString(), nurseryTimezone);
};

/**
 * Get time until token expires (in minutes)
 */
export const getTimeUntilExpiration = (token: string): number | null => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) {
    return null;
  }
  
  const now = Math.floor(Date.now() / 1000);
  const minutesLeft = Math.floor((decoded.exp - now) / 60);
  return minutesLeft;
};

/**
 * Log comprehensive token information
 */
export const logTokenInfo = () => {
  const jwtToken = localStorage.getItem('jwttoken');
  const refreshToken = localStorage.getItem('refresh_token');
  const user = localStorage.getItem('user');
  
  console.group('🔐 Token Information');
  
  if (jwtToken) {
    console.log('📄 JWT Token:', jwtToken);
    console.log('⏰ JWT Expires:', getTokenExpiration(jwtToken));
    console.log('⏱️ Minutes until expiration:', getTimeUntilExpiration(jwtToken));
    console.log('❌ Is JWT Expired:', isTokenExpired(jwtToken));
    console.log('🔍 JWT Payload:', decodeJWT(jwtToken));
  } else {
    console.log('❌ No JWT token found');
  }
  
  if (refreshToken) {
    console.log('🔄 Refresh Token:', refreshToken);
    console.log('⏰ Refresh Token Expires:', getTokenExpiration(refreshToken));
    console.log('⏱️ Refresh Minutes until expiration:', getTimeUntilExpiration(refreshToken));
    console.log('❌ Is Refresh Token Expired:', isTokenExpired(refreshToken));
  } else {
    console.log('❌ No refresh token found');
  }
  
  if (user) {
    console.log('👤 User Object:', JSON.parse(user));
  }
  
  console.groupEnd();
};

/**
 * Manually expire JWT token for testing
 */
export const expireJWTForTesting = () => {
  const user = localStorage.getItem('user');
  if (user) {
    const userObj = JSON.parse(user);
    userObj.jwttoken = 'expired_test_token';
    localStorage.setItem('user', JSON.stringify(userObj));
    localStorage.setItem('jwttoken', 'expired_test_token');
    console.log('✅ JWT token manually expired for testing');
  } else {
    console.log('❌ No user found to expire token');
  }
};

// Make functions available globally for console testing
declare global {
  interface Window {
    tokenHelper: {
      logTokenInfo: () => void;
      expireJWTForTesting: () => void;
      decodeJWT: (token: string) => any;
      isTokenExpired: (token: string) => boolean;
      getTokenExpiration: (token: string) => string | null;
      getTimeUntilExpiration: (token: string) => number | null;
    };
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  window.tokenHelper = {
    logTokenInfo,
    expireJWTForTesting,
    decodeJWT,
    isTokenExpired,
    getTokenExpiration,
    getTimeUntilExpiration,
  };
} 